import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";
import { SweepStat } from "../pages/_sweep_stat.jsx";

export function MedicalRecordsStatusCard({ matterId, showNotification }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({}); // {provider_name: bool}

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const result = await apiFetch(`/api/matters/${matterId}/records-status`);
      setData(result);
    } catch (e) {
      showNotification && showNotification(`Records status failed: ${e.message}`, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, [matterId]);

  // Color tokens per status — match the existing visual language.
  const statusStyle = (status, stale_level) => {
    if (status === "complete") {
      return { bg: C.successBg, fg: C.success, label: "Complete" };
    }
    if (status === "not_requested") {
      return { bg: "#F2F2F2", fg: C.textMuted, label: "Not Requested" };
    }
    if (stale_level === "stale_60") {
      return { bg: C.errorBg, fg: C.error, label: status === "partial" ? "Partial · 60+ days" : "Pending · 60+ days" };
    }
    if (stale_level === "stale_45") {
      return { bg: `${C.gold}30`, fg: C.goldDim, label: status === "partial" ? "Partial · 45+ days" : "Pending · 45+ days" };
    }
    if (stale_level === "stale_30") {
      return { bg: `${C.gold}18`, fg: C.goldDim, label: status === "partial" ? "Partial · 30+ days" : "Pending · 30+ days" };
    }
    return {
      bg: `${C.infoBorder}15`, fg: C.navy,
      label: status === "partial" ? "Partial" : "Pending",
    };
  };

  if (loading) {
    return React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "20px",
        border: `1px solid ${C.warmGray}`, marginBottom: "20px",
        textAlign: "center", color: C.textMuted,
      },
    },
      React.createElement(Spinner, { size: 20 }),
      React.createElement("div", { style: { fontSize: "12px", marginTop: "8px" } }, "Loading records status..."),
    );
  }

  if (!data) {
    return React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "20px",
        border: `1px solid ${C.warmGray}`, marginBottom: "20px",
        color: C.textMuted, fontSize: "13px", textAlign: "center",
      },
    }, "No records status available.");
  }

  const { summary, providers } = data;
  const hasProviders = providers && providers.length > 0;

  return React.createElement("div", {
    style: {
      background: "#fff", borderRadius: "12px", padding: "20px 24px",
      border: `1px solid ${C.warmGray}`, marginBottom: "20px",
    },
  },
    // Header
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" },
    },
      React.createElement("div", null,
        React.createElement("h2", {
          style: { fontSize: "13px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, margin: 0 },
        }, "Records Status"),
        React.createElement("div", { style: { fontSize: "11px", color: C.textLight, marginTop: "4px" } },
          `As of ${data.as_of ? new Date(data.as_of).toLocaleString() : "—"}`),
      ),
      React.createElement("button", {
        onClick: () => load(true), disabled: refreshing,
        style: {
          padding: "6px 12px", borderRadius: "6px",
          border: `1px solid ${C.warmGray}`, background: "#fff",
          color: C.navy, fontSize: "12px",
          cursor: refreshing ? "wait" : "pointer",
          display: "flex", alignItems: "center", gap: "6px",
        },
      }, refreshing ? React.createElement(Spinner, { size: 12 }) : I.refresh,
         refreshing ? "Refreshing..." : "Refresh"),
    ),

    // Summary tiles
    hasProviders && React.createElement("div", {
      style: {
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
        gap: "12px", marginBottom: "16px",
      },
    },
      React.createElement(SweepStat, { label: "Providers", value: summary.total_providers, color: C.navy }),
      React.createElement(SweepStat, { label: "Complete", value: summary.complete, color: C.success }),
      React.createElement(SweepStat, { label: "Partial", value: summary.partial, color: C.goldDim }),
      React.createElement(SweepStat, { label: "Pending", value: summary.pending, color: C.navy }),
      React.createElement(SweepStat, { label: "Not Requested", value: summary.not_requested, color: C.textMuted }),
      React.createElement(SweepStat, { label: "Stale", value: summary.stale, color: summary.stale > 0 ? C.error : C.textMuted }),
    ),

    // Provider rows
    !hasProviders ? React.createElement("div", {
      style: { padding: "20px", textAlign: "center", color: C.textMuted, fontSize: "13px" },
    }, "No providers tagged on this matter yet.")
    : React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "8px" } },
      providers.map(p => {
        const style = statusStyle(p.status, p.stale_level);
        const isOpen = !!expanded[p.provider_name];
        const docCount = (p.documents_received || []).length;
        return React.createElement("div", {
          key: p.provider_name,
          style: {
            border: `1px solid ${C.warmGray}`, borderRadius: "10px",
            background: p.is_stale ? `${style.bg}40` : "#fff",
            overflow: "hidden",
          },
        },
          React.createElement("button", {
            onClick: () => setExpanded(prev => ({ ...prev, [p.provider_name]: !prev[p.provider_name] })),
            // `record-row` reflows this on a phone: provider and status
            // badge share the top line, the two detail blocks go full
            // width beneath. See responsive.css.
            className: "record-row",
            style: {
              width: "100%", display: "grid",
              gridTemplateColumns: "1.5fr 1fr 0.8fr 100px",
              gap: "12px", alignItems: "center", padding: "12px 16px",
              border: "none", background: "transparent",
              cursor: "pointer", textAlign: "left",
            },
          },
            // Provider name
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize: "14px", fontWeight: 600, color: C.navy } },
                p.provider_name),
              React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
                p.category || "—"),
            ),
            // Request sent
            React.createElement("div", { style: { fontSize: "12px", color: C.textMuted } },
              p.request_sent_at
                ? React.createElement(React.Fragment, null,
                    React.createElement("div", { style: { fontWeight: 500, color: C.textDark } },
                      `Requested ${new Date(p.request_sent_at).toLocaleDateString()}`),
                    React.createElement("div", { style: { fontSize: "11px", marginTop: "2px" } },
                      `${p.days_since_request} day${p.days_since_request === 1 ? "" : "s"} ago`),
                  )
                : "—",
            ),
            // Documents received summary
            React.createElement("div", { style: { fontSize: "12px", color: C.textDark } },
              docCount === 0
                ? React.createElement("span", { style: { color: C.textLight } }, "Nothing received yet")
                : React.createElement(React.Fragment, null,
                    React.createElement("span", null, `${docCount} doc${docCount === 1 ? "" : "s"}`),
                    React.createElement("div", { style: { fontSize: "11px", color: C.textMuted, marginTop: "2px" } },
                      [p.has_records ? "Records ✓" : null, p.has_bills ? "Bills ✓" : null]
                        .filter(Boolean).join(" · ") || "Classification: other"),
                  ),
            ),
            // Status badge
            React.createElement("div", { style: { textAlign: "right" } },
              React.createElement("span", {
                style: {
                  display: "inline-block", padding: "4px 10px", borderRadius: "12px",
                  background: style.bg, color: style.fg,
                  fontSize: "11px", fontWeight: 600,
                },
              }, style.label),
            ),
          ),
          // Expanded document list
          isOpen && docCount > 0 && React.createElement("div", {
            style: {
              borderTop: `1px solid ${C.warmGray}`, padding: "12px 16px",
              background: C.warmWhite,
            },
          },
            React.createElement("div", {
              style: { fontSize: "11px", color: C.textMuted, fontWeight: 600, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.04em" },
            }, "Documents Received"),
            ...p.documents_received.map(d => React.createElement("div", {
              key: d.id,
              style: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: "12px" },
            },
              React.createElement("div", null,
                React.createElement("span", { style: { color: C.textDark } }, d.name),
                React.createElement("span", {
                  style: {
                    marginLeft: "8px", padding: "1px 6px", borderRadius: "8px",
                    fontSize: "10px", fontWeight: 600,
                    background: d.kind === "records" ? `${C.success}20`
                              : d.kind === "bills"   ? `${C.gold}25`
                              : C.warmGray,
                    color: d.kind === "records" ? C.success
                         : d.kind === "bills"   ? C.goldDim
                         : C.textMuted,
                  },
                }, d.kind),
              ),
              React.createElement("span", { style: { fontSize: "11px", color: C.textLight } },
                d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString() : "—"),
            )),
          ),
        );
      }),
    ),
  );
}


